import { useState, useEffect, useRef } from "react";
import { Menu, X, ArrowUpRight, Sun, Layers, Cpu, BookOpen, FileText, Download, Users, Sprout, Leaf, Briefcase, FlaskConical, Building2, BookA, Microscope, Lightbulb, User } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { supabase } from "@/integrations/supabase/client";

type IconType = typeof Sun;

interface DropdownItem {
  label: string;
  description?: string;
  href: string;
  icon?: IconType;
  code?: string;
}

interface NavItem {
  label: string;
  href?: string;
  items?: DropdownItem[];
}

const useNavItems = (): NavItem[] => {
  const { t } = useTranslation();
  return [
    {
      label: t("nav.products"),
      items: [
        { label: t("nav.items.gSeries.label"), description: t("nav.items.gSeries.description"), href: "/systems/g-series", icon: Sun, code: "SYS-01" },
        { label: t("nav.items.dSeries.label"), description: t("nav.items.dSeries.description"), href: "/systems/d-series", icon: Layers, code: "SYS-02" },
      ],
    },
    {
      label: t("nav.software", "Software"),
      items: [
        { label: t("nav.items.synthia.label"), description: t("nav.items.synthia.description"), href: "/software/synthia", icon: Cpu, code: "SFT-01" },
      ],
    },
    {
      label: t("nav.people", "People"),
      items: [
        { label: t("nav.items.team.label"), description: t("nav.items.team.description"), href: "/people/team", icon: Users, code: "PPL-01" },
        { label: t("nav.items.careers.label"), description: t("nav.items.careers.description"), href: "/people/careers", icon: Briefcase, code: "PPL-02" },
      ],
    },
    {
      label: t("nav.company"),
      items: [
        { label: t("nav.items.company.label"), description: t("nav.items.company.description"), href: "/company", icon: Building2, code: "CO-01" },
        { label: t("nav.items.story.label"), description: t("nav.items.story.description"), href: "/company/story", icon: Sprout, code: "CO-02" },
        { label: t("nav.items.esg.label"), description: t("nav.items.esg.description"), href: "/company/esg", icon: Leaf, code: "CO-03" },
      ],
    },
    {
      label: t("nav.research", "Research"),
      items: [
        { label: t("nav.items.researchPrograms.label"), description: t("nav.items.researchPrograms.description"), href: "/research/programs", icon: Microscope, code: "RND-01" },
        { label: t("nav.items.publications.label", "Publications"), description: t("nav.items.publications.description", "Peer-reviewed research and trial data"), href: "/research/publications", icon: FileText, code: "RND-02" },
        { label: t("nav.items.caseStudies.label"), description: t("nav.items.caseStudies.description"), href: "/research/case-studies", icon: FlaskConical, code: "RND-03" },
      ],
    },
    {
      label: t("nav.resources"),
      items: [
        { label: t("nav.items.articles.label"), description: t("nav.items.articles.description"), href: "/resources/articles", icon: BookOpen, code: "RES-01" },
        { label: t("nav.items.downloads.label"), description: t("nav.items.downloads.description"), href: "/resources/downloads", icon: Download, code: "RES-02" },
        { label: t("nav.items.glossary.label", "Glossary"), description: t("nav.items.glossary.description", "Plain-language explanations of lighting and CEA terms"), href: "/resources/glossary", icon: BookA, code: "RES-03" },
        { label: t("nav.items.lightPlan.label", "Light plan"), description: t("nav.items.lightPlan.description", "Get a tailored fixture layout"), href: "/resources/light-plan", icon: Lightbulb, code: "RES-04" },
      ],
    },
  ];
};

const DropdownMenu = ({ item, isActive }: { item: NavItem; isActive: boolean }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const handleEnter = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };

  const handleLeave = () => {
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        className={`relative flex h-16 items-center gap-1.5 text-[13px] tracking-tight transition-colors ${
          isActive || open ? "text-foreground" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        {item.label}
        <span
          className={`absolute -bottom-px left-0 right-0 h-px origin-left bg-primary transition-transform duration-200 ${
            open ? "scale-x-100" : "scale-x-0"
          }`}
        />
      </button>

      <div
        className={`absolute left-1/2 top-full z-50 w-[360px] -translate-x-1/2 origin-top pt-2 transition-all duration-150 ${
          open ? "pointer-events-auto opacity-100 translate-y-0" : "pointer-events-none -translate-y-1 opacity-0"
        }`}
      >
        <div className="border border-border bg-background shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
          {item.items!.map((sub, idx) => (
            <Link
              key={sub.href}
              to={sub.href}
              className={`group relative block border-l-2 border-transparent px-5 py-4 transition-colors hover:border-primary hover:bg-muted/50 ${
                idx > 0 ? "border-t border-t-border/60" : ""
              }`}
            >
              <p className="mb-1 text-[13px] font-medium tracking-tight text-foreground">
                {sub.label}
              </p>
              {sub.description && (
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  {sub.description}
                </p>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userFirstName, setUserFirstName] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const userMenuCloseTimer = useRef<number | null>(null);
  const location = useLocation();
  const { t } = useTranslation();
  const navItems = useNavItems();

  useEffect(() => {
    const extractFirstName = (user: { email?: string | null; user_metadata?: Record<string, unknown> } | null | undefined) => {
      if (!user) return null;
      const full = (user.user_metadata?.full_name as string | undefined) ?? "";
      const first = full.trim().split(/\s+/)[0];
      if (first) return first;
      const email = user.email ?? "";
      const local = email.split("@")[0] ?? "";
      return local ? local.charAt(0).toUpperCase() + local.slice(1) : null;
    };
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data.user?.email ?? null);
      setCurrentUserId(data.user?.id ?? null);
      setUserFirstName(extractFirstName(data.user));
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserEmail(session?.user.email ?? null);
      setCurrentUserId(session?.user.id ?? null);
      setUserFirstName(extractFirstName(session?.user));
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUserId) {
      setIsAdmin(false);
      return;
    }
    if (!userMenuOpen) return;

    let cancelled = false;
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", currentUserId)
      .eq("role", "admin")
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setIsAdmin(!!data);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUserId, userMenuOpen]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const isItemActive = (item: NavItem) => {
    if (item.href) return location.pathname === item.href;
    return item.items?.some((s) => location.pathname.startsWith(s.href)) ?? false;
  };

  // Page-specific sub-nav: only shown on pages that define their own in-page
  // section anchors (e.g. investor portal). Main menu sections do NOT get a sub-nav.
  type SubNavGroup = { label: string; items: { label: string; href: string }[] };
  let subNav: SubNavGroup | null = null;
  let subSubNav: SubNavGroup | null = null;
  if (location.pathname.startsWith("/company/investors") && userEmail && location.pathname !== "/company/investors/auth") {
    subNav = {
      label: t("investorsPage.subnav.label", "Investor portal"),
      items: [
        { label: t("investorsPage.subnav.chairmansLetter", "Chairman's Letter"), href: "/company/investors#ceo-letter" },
        { label: t("investorsPage.subnav.investorUpdate", "Investor Update"), href: "/company/investors#updates" },
        { label: t("investorsPage.subnav.strategy", "Strategy"), href: "/company/investors#strategy" },
        { label: t("investorsPage.subnav.market", "Market"), href: "/company/investors#market" },
        { label: t("investorsPage.subnav.roadmap", "Roadmap"), href: "/company/investors#roadmap" },
        { label: t("investorsPage.subnav.financials", "Financials"), href: "/company/investors#financials" },
        { label: t("investorsPage.subnav.capTable", "Cap Table"), href: "/company/investors#cap-table" },
        { label: t("investorsPage.subnav.governance", "Governance"), href: "/company/investors#board" },
      ],
    };
    // Financials sub-section nav
    const financialsHashes = ["#financials", "#pl", "#balance-sheet", "#cash-runway"];
    if (financialsHashes.includes(location.hash)) {
      subSubNav = {
        label: t("investorsPage.subnav.financials", "Financials"),
        items: [
          { label: t("investorsPage.subnav.pl", "P&L"), href: "/company/investors#pl" },
          { label: t("investorsPage.subnav.balanceSheet", "Balance Sheet"), href: "/company/investors#balance-sheet" },
          { label: t("investorsPage.subnav.cashRunway", "Cash Runway"), href: "/company/investors#cash-runway" },
        ],
      };
    }
    // Governance sub-section nav
    const governanceHashes = ["#board", "#general-assembly", "#articles-of-association"];
    if (governanceHashes.includes(location.hash)) {
      subSubNav = {
        label: t("investorsPage.subnav.governance", "Governance"),
        items: [
          { label: t("investorsPage.subnav.board", "Board of Directors"), href: "/company/investors#board" },
          { label: t("investorsPage.subnav.generalAssembly", "Assembly Protocols"), href: "/company/investors#general-assembly" },
          { label: t("investorsPage.subnav.articles", "Articles of Association"), href: "/company/investors#articles-of-association" },
        ],
      };
    }
  }

  return (
    <nav
      className={`fixed top-0 z-50 w-full transition-all duration-200 ${
        scrolled
          ? "border-b border-border/60 bg-background/85 backdrop-blur-xl"
          : "border-b border-transparent bg-background/60 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-8">
        <Link to="/" className="flex items-center" aria-label="Photosynthetic — Home">
          <Logo />
        </Link>

        <div className="hidden lg:flex lg:items-center lg:gap-8">
          {navItems.map((item) =>
            item.href ? (
              <Link
                key={item.label}
                to={item.href}
                className={`relative flex h-16 items-center text-[13px] tracking-tight transition-colors ${
                  isItemActive(item) ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {item.label}
              </Link>
            ) : (
              <DropdownMenu key={item.label} item={item} isActive={isItemActive(item)} />
            ),
          )}
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {userEmail ? (
            <div
              className="relative"
              onMouseEnter={() => {
                if (userMenuCloseTimer.current) {
                  window.clearTimeout(userMenuCloseTimer.current);
                  userMenuCloseTimer.current = null;
                }
                setUserMenuOpen(true);
              }}
              onMouseLeave={() => {
                userMenuCloseTimer.current = window.setTimeout(() => {
                  setUserMenuOpen(false);
                }, 200);
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  setUserMenuOpen((open) => !open);
                }}
                className="inline-flex h-10 w-10 items-center justify-center border border-border bg-background text-foreground transition-colors hover:border-primary lg:h-auto lg:w-auto lg:gap-1.5 lg:px-3 lg:py-2 lg:text-[12px] lg:font-medium lg:tracking-tight"
                aria-label="Open profile menu"
              >
                <User className="h-4 w-4 text-primary lg:h-3.5 lg:w-3.5" strokeWidth={1.5} />
                <span className="hidden max-w-[160px] truncate lg:inline">{userFirstName ?? t("userMenu.account", "Account")}</span>
              </button>
              <div
                className={`absolute right-0 top-full z-50 w-[220px] origin-top pt-2 transition-all duration-150 ${
                  userMenuOpen ? "pointer-events-auto opacity-100 translate-y-0" : "pointer-events-none -translate-y-1 opacity-0"
                }`}
              >
                <div className="border border-border bg-background shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
                  <Link
                    to="/company/investors"
                    onClick={() => setUserMenuOpen(false)}
                    className="block border-l-2 border-transparent px-4 py-3 text-[12px] font-medium tracking-tight text-foreground transition-colors hover:border-primary hover:bg-muted/50"
                  >
                    {t("userMenu.investorPortal", "Investor portal")}
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin/chat"
                      onClick={() => setUserMenuOpen(false)}
                      className="block border-t border-border/60 border-l-2 border-l-transparent px-4 py-3 text-[12px] font-medium tracking-tight text-foreground transition-colors hover:border-l-primary hover:bg-muted/50"
                    >
                      {t("userMenu.adminPanel", "Admin panel")}
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      setUserMenuOpen(false);
                      await supabase.auth.signOut();
                    }}
                    className="block w-full border-t border-border/60 border-l-2 border-l-transparent px-4 py-3 text-left text-[12px] font-medium tracking-tight text-muted-foreground transition-colors hover:border-l-primary hover:bg-muted/50 hover:text-foreground"
                  >
                    {t("userMenu.signOut", "Sign out")}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <Link
              to="/research/case-studies/auth"
              className="inline-flex h-10 w-10 items-center justify-center border border-border bg-background text-foreground transition-colors hover:border-primary lg:h-auto lg:w-auto lg:gap-1.5 lg:px-3 lg:py-2 lg:text-[12px] lg:font-medium lg:tracking-tight"
              aria-label="Sign in"
            >
              <User className="h-4 w-4 text-primary lg:h-3.5 lg:w-3.5" strokeWidth={1.5} />
              <span className="hidden lg:inline">Sign in</span>
            </Link>
          )}
          <Link
            to="/contact"
            className="group hidden items-center gap-1.5 border border-foreground/15 bg-foreground px-4 py-2 text-[12px] font-medium tracking-tight text-background transition-all hover:gap-2 hover:bg-primary lg:inline-flex"
          >
            {t("nav.contact")}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            className="-mr-2 inline-flex h-10 w-10 items-center justify-center text-foreground lg:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {subNav && (
        <div className="hidden border-t border-border/60 bg-background/85 backdrop-blur-xl lg:block">
          <div className="mx-auto flex h-11 max-w-7xl items-center gap-6 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden px-6 lg:px-8">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
              {subNav.label}
            </span>
            <div className="flex items-center gap-5">
              {subNav.items.map((sub) => {
                const active = location.pathname + location.hash === sub.href || location.pathname === sub.href;
                return (
                  <Link
                    key={sub.href}
                    to={sub.href}
                    className={`relative flex h-11 items-center whitespace-nowrap text-[12px] tracking-tight transition-colors ${
                      active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {sub.label}
                    {active && <span className="absolute -bottom-px left-0 right-0 h-px bg-primary" />}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {subSubNav && (
        <div className="hidden border-t border-border/40 bg-muted/40 backdrop-blur-xl lg:block">
          <div className="mx-auto flex h-9 max-w-7xl items-center gap-5 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden px-6 lg:px-8">
            <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              {subSubNav.label}
            </span>
            <div className="flex items-center gap-4">
              {subSubNav.items.map((sub) => {
                const active = location.pathname + location.hash === sub.href;
                return (
                  <Link
                    key={sub.href}
                    to={sub.href}
                    className={`relative flex h-9 items-center whitespace-nowrap text-[11px] tracking-tight transition-colors ${
                      active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {sub.label}
                    {active && <span className="absolute -bottom-px left-0 right-0 h-px bg-primary" />}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain border-t border-border bg-background lg:hidden">
          <div className="mx-auto max-w-7xl px-6 py-6">
            <div className="flex flex-col gap-6">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.href ? (
                    <Link
                      to={item.href}
                      onClick={() => setMobileOpen(false)}
                      className="block text-base font-medium tracking-tight text-foreground"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <>
                      <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
                        {item.label}
                      </p>
                      <div className="flex flex-col">
                        {item.items!.map((sub) => {
                          const Icon = sub.icon;
                          return (
                            <Link
                              key={sub.href}
                              to={sub.href}
                              onClick={() => setMobileOpen(false)}
                              className="flex items-start gap-3 border-t border-border py-3 first:border-t-0"
                            >
                              {Icon && <Icon className="mt-1 h-4 w-4 shrink-0 text-primary" strokeWidth={1.5} />}
                              <div>
                                <p className="text-sm font-medium text-foreground">{sub.label}</p>
                                {sub.description && (
                                  <p className="mt-0.5 text-xs text-muted-foreground">{sub.description}</p>
                                )}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-foreground px-5 py-3 text-sm font-medium text-background"
            >
              {t("nav.contact")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
